import { SearchIconGray, XIconGray } from "@/components/icons/icons";
import { HorizontalContainer } from "@/components/layout/horizontal-container";
import { HorizontalSpacer } from "@/components/layout/horizontal-spacer";
import { VerticalContainer } from "@/components/layout/vertical-container";
import { VerticalSpacer } from "@/components/layout/vertical-spacer";
import { Text } from "@/components/ui/text";
import Fuse, { type FuseResult } from "fuse.js";
import { take } from "rambda";
import React, { useEffect, useState } from "react";
import type { KeyboardEvent } from "react";
import { InputErrorMessage } from "./input-error-message";
import { InputText } from "./input-text";
import type { InputBaseProps } from "./input.model";

function localSearch(dataSource: { label: string; value: string }[], query: string) {
  const fuse = new Fuse(dataSource, { keys: ["label"] });
  return fuse.search(query);
}

interface SearchAndSelectProps extends InputBaseProps {
  searchSource: { label: string; value: string }[];
  selected: string;
  setSelected(value: string): void;
  noHitsErrorMessage?: string;
}

export const SearchAndSelect = ({
  searchSource,
  selected,
  setSelected,
  placeholder,
  id,
  name,
  disabled,
  readOnly,
  noHitsErrorMessage,
}: SearchAndSelectProps) => {
  const selectedValue = searchSource.find((source) => source.value === selected);

  //focused element with notation "<index>,<id>"
  const [focusedElement, setFocusedElement] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState(selectedValue?.label ?? "");
  const [searchResults, setSearchResults] = useState<
    FuseResult<{
      label: string;
      value: string;
    }>[]
  >([]);

  const cappedHits = take(8, searchResults);

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: This function is complex because it handles keyboard events
  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      setSearchResults([]);
    }

    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      //if not search field selected then select
      if (!focusedElement) {
        document.getElementById(id)?.focus();
        setFocusedElement(id);
        return;
      }
      //split "<index>,<id>"
      const split = focusedElement.split(",");
      if (event.key === "ArrowDown") {
        event.preventDefault();
        //if moving from search field to first result
        if (focusedElement === id && cappedHits.length > 0) {
          document.getElementById(cappedHits[0].item.label)?.focus();
          setFocusedElement(`0,${cappedHits[0].item.label}`);
          //do not move past last element
        } else if (+split[0] + 1 < cappedHits.length) {
          const index = cappedHits.findIndex((hit) => hit.item.label === focusedElement.split(",")[1]);
          document.getElementById(cappedHits[index + 1].item.label)?.focus();
          setFocusedElement(`${index + 1},${cappedHits[index + 1].item.label}`);
        }
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        //move from search result to search box
        if (focusedElement !== id && +split[0] === 0) {
          //inputfield has to be focused before setSelectionRange can be called further down
          const inputField = document.getElementById(id) as HTMLInputElement | null;
          //have to use this for setSelectionRange to work.
          setTimeout(() => {
            inputField?.focus();
          }, 0);
          //set cursor to end of query string
          inputField?.setSelectionRange(inputField?.value.length, inputField?.value.length);
          setFocusedElement(id);
          //do not move past first element
        } else if (+split[0] > 0) {
          const index = cappedHits.findIndex((hit) => hit.item.label === split[1]);
          document.getElementById(cappedHits[index - 1].item.label)?.focus();
          setFocusedElement(`${index - 1},${cappedHits[index - 1].item.label}`);
        }
      }
    }
  }

  useEffect(() => {
    if (selectedValue?.label !== searchTerm) {
      const s = localSearch(searchSource, searchTerm);
      setSearchResults(s);
    } else {
      setSearchResults([]);
    }
  }, [selectedValue, searchTerm, searchSource]);

  return (
    <div onKeyDown={(e) => handleKeyDown(e)}>
      <VerticalContainer width="full" height="full">
        <div className="search-and-select">
          <div
            aria-label="Søke-ikon"
            className="search-and-select--input-adornment search-and-select--input-adornment--left"
          >
            <SearchIconGray size="small" />
          </div>
          <InputText
            readOnly={readOnly}
            disabled={disabled}
            id={id}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setSelected("");
            }}
            value={searchTerm}
            placeholder={placeholder}
            name={name}
            weight={
              selectedValue?.label === searchTerm && selected !== "" && searchResults.length === 0 ? "bold" : "normal"
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault(); // Prevent submit form
              }
            }}
          />
          {searchTerm.length > 0 && (
            <button
              aria-label="Tøm søkefelt"
              className="search-and-select--input-adornment search-and-select--input-adornment--right"
              onClick={() => {
                setSearchTerm("");
                setSelected("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setSearchTerm("");
                  setSelected("");
                }
              }}
              type="button"
              tabIndex={0}
            >
              <XIconGray size="small" />
            </button>
          )}
        </div>
        <HorizontalSpacer size="small" />
        {searchResults.length > 0 && (
          <div className="search-and-select__results">
            {cappedHits.map((hit) => (
              <React.Fragment key={hit.item.label}>
                <LocalSearchHit
                  name={hit.item.label}
                  onSelect={() => {
                    setSelected(hit.item.value);
                    setSearchTerm(hit.item.label);
                  }}
                />
                <hr />
              </React.Fragment>
            ))}
          </div>
        )}
        {searchResults.length === 0 && selected === "" && searchTerm !== "" && (
          <InputErrorMessage externalErrorMessage={noHitsErrorMessage} />
        )}
      </VerticalContainer>
    </div>
  );
};

interface LocalSearchHitProps {
  name: string;
  onSelect: (name: string) => void;
}

const LocalSearchHit = ({ name, onSelect }: LocalSearchHitProps) => {
  return (
    <div className="search-and-select__result">
      <button
        type="button"
        id={name}
        key={name}
        onClick={() => onSelect(name)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onSelect(name);
          }
        }}
      >
        <VerticalContainer>
          <HorizontalSpacer size="small" />
          <div className="search-and-select__result--title">
            <HorizontalContainer verticalPlacement="center" noWrap={true}>
              <VerticalSpacer />
              <Text value={name} truncate={true} />
            </HorizontalContainer>
          </div>
          <HorizontalSpacer size="small" />
        </VerticalContainer>
      </button>
    </div>
  );
};
